import { useRef, useCallback, type ReactNode, type KeyboardEventHandler } from 'react';
import { createPortal } from 'react-dom';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';

export type FilterPopoverShellRenderProps = {
  requestClose: () => void;
};

export interface FilterPopoverShellProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  /** columnId, comboId и т.д. — как у ColumnWidthPopover */
  positionDependencies: unknown[];
  panelWidthPx: number;
  zIndex?: number;
  onPanelKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>, requestClose: () => void) => void;
  children: (ctx: FilterPopoverShellRenderProps) => ReactNode;
}

/**
 * Та же схема, что ColumnWidthPopover: внешний fixed + ref для позиционирования,
 * OverlayIntrinsicSize измеряет контент, внутренняя панель — анимация и разметка.
 */
export function FilterPopoverShell({
  isOpen,
  onClose,
  triggerRef,
  positionDependencies,
  panelWidthPx,
  zIndex = 1050,
  onPanelKeyDown,
  children,
}: FilterPopoverShellProps) {
  const popoverRef = useRef<HTMLDivElement>(null);

  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(
    isOpen,
    useCallback(() => onClose(), [onClose]),
    true
  );

  usePopoverPositioning({
    triggerRef,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    offset: 8,
    zIndex,
    dependencies: positionDependencies,
    /* Иначе при каждом тоггле чекбокса RO пересчитывает clamp(top) — попап «улетает» вверх */
    observePopoverResize: false,
    observeTriggerResize: false,
    repositionOnScroll: false,
  });

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    enabled: shouldRender,
    capture: false,
    excludeSelector: '[data-combo-dropdown]',
  });

  const hasAnchor = !!triggerRef.current;
  const anchorStyle: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: 'auto',
    left: hasAnchor ? -9999 : 8,
    top: hasAnchor ? -9999 : 8,
  };

  const panelStyle: React.CSSProperties = {
    width: panelWidthPx,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    maxHeight: 'min(640px, calc(100vh - 32px))',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> | undefined = onPanelKeyDown
    ? (e) => onPanelKeyDown(e, requestClose)
    : undefined;

  if (!shouldRender) return null;

  return createPortal(
    <div ref={popoverRef} style={anchorStyle}>
      <OverlayIntrinsicSize className="filter-popover-shell__intrinsic">
        <div
          className={`filter-popover editable-cell-popover ${contentClasses}`}
          style={panelStyle}
          onKeyDown={handleKeyDown}
        >
          {children({ requestClose })}
        </div>
      </OverlayIntrinsicSize>
    </div>,
    document.body
  );
}
